import { MapPin } from "@/components/ui/icons";
import type { ReactNode } from "react";
import { ScrollView, View } from "react-native";

import { FilterPill } from "@/components/filters/FilterPill";
import { MenuPill } from "@/components/filters/MenuPill";
import { VENDOR_STOCK_GROUP_NOUN } from "@/lib/inventory/stockGroupNoun";
import { useThemeColors } from "@/theme/theme";

/** Up to this many Locations get one-tap pills; more get one `MenuPill` that lists them all. */
export const LOCATION_PILLS_MAX = 4;

/** The menu's value for "every Location", which no StockGroup name can be. */
const ALL = "";

/**
 * The storefront's control row: `sort`, then the Location filter (TRO-1095), then the applied
 * filter `chips`, in one row that scrolls sideways (TRO-1121) because the fixed header already costs
 * a phone a third of its height. The row bleeds past the header's 16px gutter to the screen edges,
 * so a pill cut off at the edge reads as more to scroll rather than as a clipped layout.
 *
 * A customer told "it's in the showcase" narrows by Location, so how they switch depends on how
 * many there are:
 *
 * - Up to `LOCATION_PILLS_MAX`: one pill each, after the chips, one tap away. There is no "All"
 *   pill; tapping the selected pill clears it.
 * - More: a "Location: All" `MenuPill` right after the sort, listing every Location. A table with
 *   thirteen ("Pokémon JP $20-$50", "Slabs") took swipes to reach the last pills, and the selected
 *   one scrolled out of sight; the menu reaches any of them in two taps and always names the current
 *   one.
 *
 * The page is always a Vendor's table, so it reads the Vendor noun rather than
 * `useStockGroupNoun()`, which needs a signed-in workspace. Pass no `locations` for a table with
 * fewer than two; the row then holds `sort` and `chips` alone.
 */
export function BrowseLocationPills({
  locations,
  selected,
  onChange,
  sort,
  chips,
}: {
  locations: string[];
  selected: string | null;
  onChange: (location: string | null) => void;
  sort: ReactNode;
  chips: ReactNode;
}) {
  const c = useThemeColors();
  const noun = VENDOR_STOCK_GROUP_NOUN;
  const menu = locations.length > LOCATION_PILLS_MAX;
  return (
    <ScrollView
      testID="browse-location-pills"
      horizontal
      showsHorizontalScrollIndicator={false}
      className="-mx-4 grow-0"
      contentContainerClassName="items-center gap-2 px-4"
    >
      {sort}
      {menu
        ? (
          <MenuPill
            testID="browse-location-menu"
            prefix={noun.one}
            icon={<MapPin size={14} color={selected ? c.primary : c.mutedForeground} aria-hidden />}
            label={selected ?? "All"}
            a11yLabel={`${noun.one}, ${selected ?? `all ${noun.lowerMany}`}`}
            a11yHint={`Shows one ${noun.lowerOne} of this table`}
            title={`Choose a ${noun.lowerOne}`}
            tinted={selected != null}
            value={selected ?? ALL}
            onPick={(value) => onChange(value === ALL ? null : value)}
            options={[
              { value: ALL, label: `All ${noun.lowerMany}`, a11yLabel: `All ${noun.lowerMany}` },
              ...locations.map((location) => ({
                value: location,
                label: location,
                a11yLabel: `Filter by ${noun.lowerOne} ${location}`,
              })),
            ]}
          />
        )
        : null}
      {chips}
      {!menu && locations.length > 0
        ? (
          <>
            {/* A rule between the actions and the one-tap Location pills. */}
            <View aria-hidden className="mx-1 h-6 w-px bg-border" />
            {locations.map((location) => (
              <FilterPill
                key={location}
                label={location}
                a11yLabel={`Filter by ${noun.lowerOne} ${location}`}
                active={selected === location}
                // Tapping the selected pill again clears it, the quick way back to everything.
                onPress={() => onChange(selected === location ? null : location)}
              />
            ))}
          </>
        )
        : null}
    </ScrollView>
  );
}
